import _ from 'lodash';
import moment from 'moment-timezone';
import {
  AUTHORIZED_PANEL_REVIEW_ROLES,
  STATUS_SUBMITTED_TO_FEMA,
  BEFORE_FINAL_SUBMISSION_STATUSES,
  AUTHORIZED_ROLES,
  ENTITY_MAP,
  ROLE_MAP,
  AUTHORIZED_SUBRECIPIENT_ROLES,
  AUTHORIZED_VIEW_SUB_APP_ROLES,
  HMA_PROGRAMS,
  AUTHORIZED_ROLES_V2,
  IA_PROGRAMS,
  STATUS_CLOSEOUT_INITIATED,
  STATUS_PENDING_SUBMISSION,
  STATUS_PENDING_AOR_SUBMISSION,
  PROGRAM_TYPE_PASSTHROUGH,
} from 'app-ui-componentlibrary/dist/common/constants';
import {
  formatDateMMDDYYYY,
  getUnixtimeFromISO,
  isFemaUser,
  isProgramExternal,
  isUserOrgPrgSuborgSelfSarSm,
  isUserOrgProgramAllowed,
  isUserRoleAllowed,
  retrieveAllEntityIdsFromPermissions,
  retrieveEntityTypeIdsFromPermissions,
  retrieveRolesFromPermissions,
} from 'common/utils';
import goStore from 'go/store';
import {unpartitionedToggleIsOn} from 'common/storeUtils';

export const canViewPanelCoordinatorPage = (permissions, panelSession) => {
  return permissions.includes(
    `${ENTITY_MAP.PANEL_REVIEW.code}:${panelSession}:${ROLE_MAP.PRC.code}`
  );
};

export const canViewPanelReview = (permissions, panelSession, toggledV2 = false) => {
  const {previewDate, endDate} = panelSession;
  let sessionId;
  if (toggledV2) {
    sessionId = panelSession.sessionId;
  } else {
    sessionId = panelSession.id;
  }
  // check if any of the permissions has entity type = panel review
  if (
    !_.isEmpty(
      _.intersection(
        [ENTITY_MAP.PANEL_REVIEW.code],
        retrieveAllEntityIdsFromPermissions(permissions)
      )
    )
  ) {
    // get permissions only for panel review entities and it matches the panel session
    let panelSessionPermissions = _.filter(permissions, (permission) => {
      let [entityTypeId, userPanelSessionId, role] = permission.split(':');
      if (
        entityTypeId == ENTITY_MAP.PANEL_REVIEW.code &&
        (role == ROLE_MAP.PC.code ||
          role == ROLE_MAP.PR.code ||
          role == ROLE_MAP.PRC.code) &&
        userPanelSessionId == sessionId
      ) {
        return permission;
      }
    });

    return (
      !_.isEmpty(panelSessionPermissions) &&
      withinPanelReviewDateRange(previewDate, endDate)
    );
  }
  return false;
};

export const canViewPanelChairReview = (permissions) => {
  return compareRolesAndPermissions([ROLE_MAP.PC.code], permissions);
};

export const canViewPanelReviews = (permissions) => {
  return compareRolesAndPermissions([ROLE_MAP.PR.code], permissions);
};

export const isAOR = (permissions) => {
  return compareRolesAndPermissions([ROLE_MAP.ORGAOR.code], permissions);
};

export const isSAR = (permissions) => {
  return compareRolesAndPermissions([ROLE_MAP.SUBAUTHREP.code], permissions);
};

export const isSM = (permissions) => {
  return compareRolesAndPermissions([ROLE_MAP.SUBMEMBER.code], permissions);
};

export const isSPGA = (permissions) => {
  return compareRolesAndPermissions([ROLE_MAP.SPGMADMIN.code], permissions);
};

export const isSMA = (permissions) => {
  return compareRolesAndPermissions([ROLE_MAP.SMA.code], permissions);
};

export const isAOorSAOorGMS = (permissions) => {
  return (
    compareRolesAndPermissions([ROLE_MAP.PGMAO.code], permissions) ||
    compareRolesAndPermissions([ROLE_MAP.SAO.code], permissions) ||
    compareRolesAndPermissions([ROLE_MAP.PGMGMS.code], permissions)
  );
};

export const isIAProgram = (programId) => {
  if (!programId) return false;
  const IA_program_codes = [IA_PROGRAMS.DCM.code];
  return _.includes(IA_program_codes, programId.toString());
};

export const isSubAOR = (permissions) => {
  return compareRolesAndPermissions(
    [ROLE_MAP.SUBAUTHREP.code, ROLE_MAP.SUBMEMBER.code],
    permissions
  );
};

export const isRecipient = (permissions) => {
  return compareRolesAndPermissions(
    [
      ROLE_MAP.ORGAOR.code,
      ROLE_MAP.ORGFINMEMBER.code,
      ROLE_MAP.ORGMEMBER.code,
      ROLE_MAP.ORGPGMMEMBER.code,
    ],
    permissions
  );
};

export const isEntityRoleAvailable = (permissions, orgId, roleId) => {
  return isUserRoleAllowed(permissions, orgId, roleId);
};

export const isExternal = (permissions) => {
  return !isFemaUser(permissions);
};

export const isOrgProgramAOR = (permissions, orgId, roleId) => {
  return isUserOrgProgramAllowed(permissions, orgId, roleId);
};

export const isUserSelfSarSm = (permissions, suborgId, roleId) => {
  return isUserOrgPrgSuborgSelfSarSm(permissions, suborgId, roleId);
};

export const hasAuthorizationToViewSubapplication = (permissions) => {
  return compareRolesAndPermissions(AUTHORIZED_VIEW_SUB_APP_ROLES, permissions);
};

export const canViewBucketTracker = (permissions) => {
  return compareRolesAndPermissions(
    [
      ROLE_MAP.PGMAO.code,
      ROLE_MAP.PGMADMIN.code,
      ROLE_MAP.GCA.code,
      ROLE_MAP.ASA.code,
      ROLE_MAP.SPGMADMIN.code,
    ],
    permissions
  );
};

//awardTrackerConfiguration toggle, when remove replace above canViewBucketTracker
export const canViewAwardsTracker = (permissions) => {
  return compareRolesAndPermissions(
    [
      ROLE_MAP.PGMAO.code,
      ROLE_MAP.PGMADMIN.code,
      ROLE_MAP.GCA.code,
      ROLE_MAP.ASA.code,
      ROLE_MAP.SPGMADMIN.code,
      ROLE_MAP.SRANALYTICSADMIN.code,
      ROLE_MAP.ANALYTICSADMIN.code,
    ],
    permissions
  );
};

export const canViewTasksTab = (permissions) => {
  return (
    compareEntityIdsAndPermissions(
      [ENTITY_MAP.PROGRAM.code, ENTITY_MAP.PROGRAM_EXTERNAL.code, ENTITY_MAP.SYSTEM.code],
      permissions
    ) ||
    compareRolesAndPermissions(
      [ROLE_MAP.ANALYTICSADMIN.code, ROLE_MAP.SRANALYTICSADMIN.code],
      permissions
    )
  );
};

export const canViewPanelAdminTab = (permissions) => {
  return compareRolesAndPermissions([ROLE_MAP.PANELADMIN.code], permissions);
};

export const canViewAdminConsoleTab = (permissions) => {
  return compareRolesAndPermissions([ROLE_MAP.SYSTEMADMIN.code], permissions);
};

export const canViewAdminConsoleTab2 = (permissions) => {
  return (
    (compareEntityIdsAndPermissions([ENTITY_MAP.PROGRAM.code], permissions) &&
      compareRolesAndPermissions([ROLE_MAP.SPGMADMIN.code], permissions)) ||
    compareRolesAndPermissions([ROLE_MAP.SMA.code], permissions)
  );
};

export const canViewGrantsTab = (permissions) => {
  return compareEntityIdsAndPermissions(
    [
      ENTITY_MAP.ORGANIZATION.code,
      ENTITY_MAP.PROGRAM_ORGANIZATION.code,
      ENTITY_MAP.PROGRAM_ORGANIZATION_SUBORGANIZATION.code,
    ],
    permissions
  );
};

export const canViewTeamTab = (permissions) => {
  return (
    compareEntityIdsAndPermissions(
      [ENTITY_MAP.PROGRAM.code, ENTITY_MAP.REPORT_CATEGORY.code],
      permissions
    ) &&
    (canViewTeamPage(permissions) || isAnalyticsAdmin(permissions))
  );
};

export const canViewTeamTabOld = (permissions) => {
  return (
    compareEntityIdsAndPermissions(
      [
        ENTITY_MAP.ORGANIZATION.code,
        ENTITY_MAP.PROGRAM_ORGANIZATION.code,
        ENTITY_MAP.PROGRAM_ORGANIZATION_SUBORGANIZATION.code,
      ],
      permissions
    ) && canViewTeamPage(permissions)
  );
};

export const canViewOrganizationsTab = (permissions) => {
  return compareEntityIdsAndPermissions(
    [
      ENTITY_MAP.ORGANIZATION.code,
      ENTITY_MAP.PROGRAM_ORGANIZATION.code,
      ENTITY_MAP.PROGRAM_ORGANIZATION_SUBORGANIZATION.code,
    ],
    permissions
  );
};

export const canViewThingsToDo = (permissions) => {
  if (
    compareRolesAndPermissions(
      [
        ROLE_MAP.ORGAOR.code,
        ROLE_MAP.ORGMEMBER.code,
        ROLE_MAP.ORGPGMMEMBER.code,
        ROLE_MAP.ORGFINMEMBER.code,
      ],
      permissions
    ) ||
    !_.isEmpty(
      _.intersection(
        AUTHORIZED_SUBRECIPIENT_ROLES,
        retrieveRolesFromPermissions(permissions)
      )
    )
  ) {
    return compareEntityIdsAndPermissions(
      [
        ENTITY_MAP.ORGANIZATION.code,
        ENTITY_MAP.PROGRAM_ORGANIZATION.code,
        ENTITY_MAP.PROGRAM_ORGANIZATION_SUBORGANIZATION.code,
      ],
      permissions
    );
  }
};

export const checkThingsToDoRoles = (permissions, orgId) => {
  return hasRolesForOrg(
    permissions,
    [
      ROLE_MAP.ORGAOR.code,
      ROLE_MAP.ORGPGMMEMBER.code,
      ROLE_MAP.ORGMEMBER.code,
      ROLE_MAP.ORGFINMEMBER.code,
    ],
    orgId
  );
};

export const canViewReportsTab = (permissions) => {
  return canViewAnalytics(permissions);
};

export const canViewAnalytics = (permissions) => {
  return compareRolesAndPermissions(
    [ROLE_MAP.ANALYTICSUSER.code, ROLE_MAP.ANALYTICSADMIN.code],
    permissions
  );
};

export const withinPanelReviewDateRange = (previewDate, endDate) => {
  const todayDateUnixtime = getUnixtimeFromISO();
  const previewDateUnixtime = getUnixtimeFromISO(previewDate);
  const endDateUnixtime = getUnixtimeFromISO(endDate);

  return todayDateUnixtime >= previewDateUnixtime && todayDateUnixtime <= endDateUnixtime;
};

export const isAnalyticsAdmin = (permissions) => {
  return compareRolesAndPermissions([ROLE_MAP.ANALYTICSADMIN.code], permissions);
};

export const isOnlyProgramExternal = (permissions) => {
  if (!isProgramExternal(permissions)) {
    return false;
  }
  let isOnlyProgramExternal = true;
  _.forEach(retrieveEntityTypeIdsFromPermissions(permissions), (entityTypeId) => {
    if (entityTypeId !== ENTITY_MAP.PROGRAM_EXTERNAL.code) {
      isOnlyProgramExternal = false;
      return false;
    }
  });
  return isOnlyProgramExternal;
};

export const rfiSectionCheck = (permissions, orgId, subOrgId) => {
  const allowedRoles = [
    ROLE_MAP.ORGAOR.code,
    ROLE_MAP.ORGPGMMEMBER.code,
    ROLE_MAP.ORGFINMEMBER.code,
    ROLE_MAP.ORGMEMBER.code,
    ROLE_MAP.GCA.code,
    ROLE_MAP.GCS.code,
  ];

  const allowedSARRoles = [ROLE_MAP.SUBAUTHREP.code, ROLE_MAP.SUBMEMBER.code];
  const allowedEntityTypes = [
    ENTITY_MAP.PROGRAM_ORGANIZATION.code,
    ENTITY_MAP.ORGANIZATION.code,
  ];
  if (_.isEmpty(permissions)) {
    return false;
  } else {
    return _.chain(permissions)
      .map((permission) => {
        let [entityTypeId, entityId, role] = permission.split(':');
        const entityIdFromPermission = _.chain(entityId).split('_').get(0).value();
        const suborgIdFromPermission = _.chain(entityId).split('_').get(2).value();
        if (
          entityIdFromPermission === _.toString(orgId) &&
          _.isUndefined(suborgIdFromPermission) &&
          _.includes(allowedEntityTypes, entityTypeId) &&
          _.includes(allowedRoles, role)
        ) {
          return true;
        } else if (
          entityIdFromPermission === suborgIdFromPermission &&
          entityIdFromPermission === _.toString(orgId) &&
          suborgIdFromPermission === _.toString(orgId) &&
          entityTypeId === ENTITY_MAP.PROGRAM_ORGANIZATION_SUBORGANIZATION.code &&
          _.includes(allowedSARRoles, role)
        ) {
          return true;
        } else if (isFemaUser(permissions)) {
          return true;
        } else {
          return false;
        }
      })
      .some((p) => p === true)
      .value();
  }
};

const compareRolesAndPermissions = (roles, permissions) => {
  return !_.isEmpty(
    _.intersectionWith(roles, retrieveRolesFromPermissions(permissions), (x, y) =>
      _.isEqual(_.toString(x), _.toString(y))
    )
  );
};

const compareEntityIdsAndPermissions = (entityIds, permissions) => {
  return !_.isEmpty(
    _.intersection(retrieveAllEntityIdsFromPermissions(permissions), entityIds)
  );
};

const compareRolesAndPermissionsForEntity = (roles, permissions, entityId) => {
  const roleRegex = /[^:]*$/;
  const entityIdRegex = /(:)(.*)(:)/;

  return (
    _.size(
      _.filter(
        permissions,
        (perm) =>
          _.find(
            roles,
            (role) => _.toNumber(role) === _.toNumber(perm.match(roleRegex)[0])
          ) !== undefined &&
          _.toNumber(perm.match(entityIdRegex)[2]) === _.toNumber(entityId)
      )
    ) > 0
  );
};

const compareRolesAndProgramsForEntity = (roles, permissions, entityId, programId) => {
  const roleRegex = /[^:]*$/;
  const entityIdRegex = /(:)(.*)(:)/;
  const entityTypeRegex = /[^:]*/;

  return (
    _.size(
      _.filter(
        permissions,
        (perm) =>
          _.find(
            roles,
            (role) => _.toNumber(role) === _.toNumber(perm.match(roleRegex)[0])
          ) !== undefined &&
          ((_.toNumber(perm.match(entityTypeRegex)[0]) === 1 &&
            _.toNumber(perm.match(entityIdRegex)[2]) === _.toNumber(entityId)) ||
            (_.toNumber(perm.match(entityTypeRegex)[0]) === 5 &&
              perm.match(entityIdRegex)[2] === `${entityId}_${programId}`))
      )
    ) > 0
  );
};

const compareRolesAndProgramsForSubEntity = (
  roles,
  permissions,
  subEntityId,
  programId,
  entityId
) => {
  const roleRegex = /[^:]*$/;
  const entityIdRegex = /(:)(.*)(:)/;
  const entityTypeRegex = /[^:]*/;

  return (
    _.size(
      _.filter(
        permissions,
        (perm) =>
          _.find(
            roles,
            (role) => _.toNumber(role) === _.toNumber(perm.match(roleRegex)[0])
          ) !== undefined &&
          _.toNumber(perm.match(entityTypeRegex)[0]) === 6 &&
          perm.match(entityIdRegex)[2] === `${entityId}_${programId}_${subEntityId}`
      )
    ) > 0
  );
};

export const extractOrgIdsFromPermissions = (permissions) => {
  // Takes in permissions
  // Returns a list of distinct orgIds that appear in the permissions

  const entityTypeRegex = /[^:]*/;
  const entityIdRegex = /(:)(.*)(:)/;

  if (_.isEmpty(permissions)) {
    return [];
  } else {
    return _.uniq(
      _.map(
        _.filter(
          permissions,
          (perm) =>
            _.toNumber(perm.match(entityTypeRegex)[0]) === 1 ||
            _.toNumber(perm.match(entityTypeRegex)[0]) === 5
        ),
        (perm) => {
          return _.toNumber(perm.match(entityIdRegex)[2].split('_')[0]);
        }
      )
    );
  }
};

export const extractSubOrgIdsFromPermissions = (permissions) => {
  // Takes in permissions, filtering out
  // Returns a list of distinct subOrgIds that appear in the permissions

  const entityTypeRegex = /[^:]*/;
  const entityIdRegex = /(:)(.*)(:)/;

  if (_.isEmpty(permissions)) {
    return [];
  } else {
    return _.uniq(
      _.map(
        _.filter(permissions, (perm) => _.toNumber(perm.match(entityTypeRegex)[0]) === 6),
        (perm) => {
          return _.toNumber(perm.match(entityIdRegex)[2].split('_')[2]);
        }
      )
    );
  }
};

export const hasInternalFundingOpIds = (permissions) => {
  // back end verifies right funding op id for the entity, so no need to check the specific
  // funding op id here
  return !!_.chain(permissions)
    .map((permission) => {
      let entityTypeId = permission.split(':')[0];
      return entityTypeId === ENTITY_MAP.FUNDING_OPPORTUNITY.code;
    })
    .filter((value) => value !== undefined && value === true)
    .uniq()
    .value()[0];
};

export const hasHMAprogramIds = (permissions) => {
  const HMA_program_codes = [
    HMA_PROGRAMS.PDM.code,
    HMA_PROGRAMS.FMA.code,
    HMA_PROGRAMS.BRIC.code,
    HMA_PROGRAMS.HMGP.code,
    HMA_PROGRAMS.HMPF.code,
  ];

  const hasEntityType1 = _.includes(
    retrieveAllEntityIdsFromPermissions(permissions),
    ENTITY_MAP.ORGANIZATION.code
  );

  const has_NTR_role = compareRolesAndPermissions(
    [ROLE_MAP.NTRREVIEWER.code],
    permissions
  );

  const hasProgramId = (permissions) => {
    return permissions.some((permission) => {
      //match function isolates the entity id from permission [5:-51256_10:18] -> [-51256_10]
      //split the entity id to extract program id [-51256_10] -> [10]
      let programId = _.split(_.get(permission.match(/:(.*?):/), 1), '_')[1];
      return (
        programId && !_.isUndefined(programId) && _.includes(HMA_program_codes, programId)
      );
    });
  };

  //return false if a permission lacks programId
  //return true if a user has multiple permissions and at least one is valid
  // 5:-51256_10:18 split into --> [5:-51256, 10:18], then takes the first two characters from the 'second' element

  let result = [];

  if (hasEntityType1 || isFemaUser(permissions)) {
    return true;
  } else if (isProgramExternal(permissions) && has_NTR_role) {
    permissions.every((permission) => result.push(permission.split(':')[1]));
    return !_.isEmpty(_.intersection(result, HMA_program_codes));
  } else {
    return hasProgramId(permissions);
  }
};

export const canViewSubGrantAmendmentsAndPayments = (permissions) => {
  return compareRolesAndPermissions(
    [
      ROLE_MAP.SUBAUTHREP.code,
      ROLE_MAP.SUBMEMBER.code,
      ROLE_MAP.ORGAOR.code,
      ROLE_MAP.ORGMEMBER.code,
      ROLE_MAP.ORGPGMMEMBER.code,
      ROLE_MAP.ORGFINMEMBER.code,
    ],
    permissions
  );
};
export const canViewPostAwardEntities = (permissions) => {
  return compareRolesAndPermissions(
    [
      ROLE_MAP.ORGAOR.code,
      ROLE_MAP.ORGMEMBER.code,
      ROLE_MAP.ORGPGMMEMBER.code,
      ROLE_MAP.ORGFINMEMBER.code,
    ],
    permissions
  );
};

export const canCreateSubgrantPaymentRequest = (permissions, orgId, programId) => {
  return compareRolesAndProgramsForEntity(
    [
      ROLE_MAP.ORGAOR.code,
      ROLE_MAP.ORGFINMEMBER.code,
      ROLE_MAP.ORGPGMMEMBER.code,
      ROLE_MAP.ORGMEMBER.code,
    ],
    permissions,
    orgId,
    programId
  );
};

export const canViewRFI = (permissions, orgId, programId) => {
  return compareRolesAndProgramsForEntity(
    [ROLE_MAP.ORGAOR.code, ROLE_MAP.ORGPGMMEMBER.code],
    permissions,
    orgId,
    programId
  );
};

export const canEditPropertyData = (permissions) => {
  return compareRolesAndPermissions(
    [ROLE_MAP.ORGAOR.code, ROLE_MAP.SUBAUTHREP.code],
    permissions
  );
};

export const hasRolesForRFI = (permissions) => {
  return compareRolesAndPermissions(
    [ROLE_MAP.ORGAOR.code, ROLE_MAP.ORGPGMMEMBER.code],
    permissions
  );
};

export const canReopenApplications = (
  permissions,
  status,
  aplcnEndDate,
  toggles,
  foId,
  programId,
  programType
) => {
  const appEndDate = moment(aplcnEndDate, 'YYYY-MM-DDTHH:mm:ssZ');
  const now = moment();
  return (
    isAOR(permissions) &&
    _.isEqual(STATUS_SUBMITTED_TO_FEMA, status) &&
    !isIAProgram(programId) &&
    now.isBefore(appEndDate) &&
    !_.isEqual(programType, PROGRAM_TYPE_PASSTHROUGH)
  );
};

export const canCancelCloseoutReport = (permissions, status, closeoutReportStatus) => {
  return (
    canViewPostAwardEntities(permissions) &&
    _.isEqual(STATUS_CLOSEOUT_INITIATED, status) &&
    closeoutReportStatus !== undefined &&
    (_.isEqual(closeoutReportStatus, STATUS_SUBMITTED_TO_FEMA) ||
      _.isEqual(closeoutReportStatus, STATUS_PENDING_SUBMISSION))
  );
};

export const canCancelSubCloseoutReport = (permissions, orgId) => {
  return hasRolesForOrg(
    permissions,
    [
      ROLE_MAP.ORGAOR.code,
      ROLE_MAP.ORGPGMMEMBER.code,
      ROLE_MAP.ORGMEMBER.code,
      ROLE_MAP.ORGFINMEMBER.code,
    ],
    orgId
  );
};

export const canContinueApplications = (
  permissions,
  status,
  aplcnEndDate,
  toggles,
  foId
) => {
  const appEndDate = moment(aplcnEndDate, 'YYYY-MM-DDTHH:mm:ssZ');
  const now = moment();
  return (
    (toggles || true) &&
    canViewAndContinueApplication(permissions) &&
    _.includes(BEFORE_FINAL_SUBMISSION_STATUSES, status) &&
    now.isBefore(appEndDate)
  );
};

export const canViewAndContinueApplication = (permissions) => {
  return compareRolesAndPermissions(
    [
      ROLE_MAP.ORGAOR.code,
      ROLE_MAP.ORGMEMBER.code,
      ROLE_MAP.ORGPGMMEMBER.code,
      ROLE_MAP.ORGFINMEMBER.code,
      ROLE_MAP.ORGGRNTWRITER.code,
    ],
    permissions
  );
};
export const canContinueSubapplicationSubmitted = (
  permissions,
  currentOrgId,
  rolesToCheck,
  programId,
  parentOrgId
) => {
  const selectedOrgPermission = _.filter(permissions, (p) => {
    return _.includes(p, currentOrgId || '');
  });
  return programId
    ? compareRolesAndProgramsForSubEntity(
        rolesToCheck,
        permissions,
        currentOrgId,
        programId,
        parentOrgId
      ) ||
        compareRolesAndProgramsForSubEntity(
          rolesToCheck,
          permissions,
          parentOrgId,
          programId,
          parentOrgId
        )
    : compareRolesAndPermissions(rolesToCheck, selectedOrgPermission);
};

// AUTHORIZED_ROLES - 1, 5
export const canViewSubapplication = (permissions) => {
  return !_.isEmpty(
    _.intersection(
      AUTHORIZED_VIEW_SUB_APP_ROLES,
      retrieveRolesFromPermissions(permissions)
    )
  );
};

export const canViewAndContinueSubapplication = (permissions) => {
  return !_.isEmpty(
    _.intersection(
      AUTHORIZED_SUBRECIPIENT_ROLES,
      retrieveRolesFromPermissions(permissions)
    )
  );
};

export const canSubmitSubapplicationsToFEMA = (
  permissions,
  organizationId,
  programId
) => {
  return compareRolesAndProgramsForEntity(
    [ROLE_MAP.ORGAOR.code],
    permissions,
    organizationId,
    programId
  );
};

export const canStartSubapplication = (permissions, organizationId, programId) => {
  return compareRolesAndProgramsForSubEntity(
    [ROLE_MAP.SUBAUTHREP.code, ROLE_MAP.SUBMEMBER.code],
    permissions,
    organizationId,
    programId,
    organizationId
  );
};

export const canViewTeamPage = (permissions) => {
  return !_.isEmpty(
    _.intersection(
      unpartitionedToggleIsOn(goStore.store.getState(), 'panelAdminV3') // TODO - Please remove this once panel admin v3 goes live
        ? AUTHORIZED_ROLES_V2
        : AUTHORIZED_ROLES,
      retrieveRolesFromPermissions(permissions)
    )
  );
};

export const hasGeneralPermission = (permission) => {
  const store = goStore.store.getState();

  const toggles = _.get(store, 'toggles.toggles');
  const teamManagementExpansion = _.includes(toggles, 'teamManagementExpansion');
  if (!teamManagementExpansion) return false;

  const generalPermissions = _.get(store, 'auth.generalPermissions');

  return _.includes(generalPermissions, permission);
};

export const hasScopedRoles = (scope, roles) => {
  return !_.chain(roles)
    .flatMap((role) => [
      `${ENTITY_MAP.PROGRAM.code}:${_.get(scope, 'programId')}:${role}`,
      `${ENTITY_MAP.FUNDING_OPPORTUNITY.code}:${_.get(
        scope,
        'fundingOpportunityId'
      )}:${role}`,
    ])
    .intersection(_.get(scope, 'permissions'))
    .isEmpty()
    .value();
};

export const isStrictlyRolesForOrg = (permissions, roles, orgId) => {
  //returns true if there's strictly at least one permission for the given roles and given organization
  const roleRegex = /[^:]*$/;
  const orgRegex = /:([^_|:]*)/;
  let hasAStrictlyRole = false;

  const permissionsWithOrgId = _.filter(permissions, (permission) => {
    const permissionOrgId = _.toNumber(permission.match(orgRegex)[1]);
    return permissionOrgId === _.toNumber(orgId);
  });

  for (const permission of permissionsWithOrgId) {
    const permissionRole = _.toNumber(permission.match(roleRegex)[0]);
    if (_.includes(roles, permissionRole)) {
      hasAStrictlyRole = true;
    } else return false;
  }

  return hasAStrictlyRole;
};

export const hasRolesForOrg = (permissions, roles, orgId) => {
  //returns true if there's at least one permission for the given roles and given organization
  const roleRegex = /[^:]*$/;
  const orgRegex = /:([^_|:]*)/;
  const rolesToNum = _.map(_.values(roles), _.toNumber);

  for (const permission of permissions) {
    const permissionOrgId = _.toNumber(permission.match(orgRegex)[1]);
    const permissionRole = _.toNumber(permission.match(roleRegex)[0]);
    if (_.includes(rolesToNum, permissionRole) && permissionOrgId === _.toNumber(orgId))
      return true;
  }
  return false;
};

export const canInitiateOrContinueSubCloseoutReport = (permissions, orgId) => {
  return hasRolesForOrg(
    permissions,
    [
      ROLE_MAP.ORGAOR.code,
      ROLE_MAP.ORGPGMMEMBER.code,
      ROLE_MAP.ORGMEMBER.code,
      ROLE_MAP.ORGFINMEMBER.code,
    ],
    orgId
  );
};

export default {
  canViewPanelChairReview,
  canViewPanelReviews,
  isAOR,
  isEntityRoleAvailable,
  hasAuthorizationToViewSubapplication,
  canSubmitSubapplicationsToFEMA,
  canViewBucketTracker,
  canViewTeamPage,
  compareRolesAndPermissions,
  compareRolesAndPermissionsForEntity,
  compareRolesAndProgramsForEntity,
  compareRolesAndProgramsForSubEntity,
  extractOrgIdsFromPermissions,
  extractSubOrgIdsFromPermissions,
  isExternal,
  canViewAnalytics,
  hasGeneralPermission,
  isStrictlyRolesForOrg,
  hasRolesForOrg,
  canStartSubapplication,
};
